import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { PageHeader, StatusChip, LoadingBlock } from "@/components/Common";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, FileEdit, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { fmtDate } from "@/lib/utils-app";

const empty = { title: "", excerpt: "", body_md: "", cover_emoji: "✨", tags: "", status: "draft", slug: "" };

export default function BlogAdmin() {
    const [items, setItems] = useState(null);
    const [open, setOpen] = useState(false);
    const [edit, setEdit] = useState(empty);
    const [editingId, setEditingId] = useState(null);

    const load = () => api.get("/blog/admin/posts").then((r) => setItems(r.data));
    useEffect(() => {
        load();
    }, []);

    const openCreate = () => {
        setEdit(empty);
        setEditingId(null);
        setOpen(true);
    };

    const openEdit = (p) => {
        setEdit({
            title: p.title, excerpt: p.excerpt, body_md: p.body_md,
            cover_emoji: p.cover_emoji, tags: (p.tags || []).join(", "),
            status: p.status, slug: p.slug,
        });
        setEditingId(p.id);
        setOpen(true);
    };

    const save = async () => {
        const payload = {
            ...edit,
            tags: edit.tags ? edit.tags.split(",").map((s) => s.trim()).filter(Boolean) : [],
        };
        try {
            if (editingId) {
                await api.patch(`/blog/admin/posts/${editingId}`, payload);
                toast.success("Post updated");
            } else {
                await api.post("/blog/admin/posts", payload);
                toast.success("Post created");
            }
            setOpen(false);
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Save failed");
        }
    };

    const remove = async (p) => {
        if (!window.confirm(`Delete '${p.title}'?`)) return;
        await api.delete(`/blog/admin/posts/${p.id}`);
        toast.success("Post deleted");
        load();
    };

    return (
        <>
            <PageHeader
                eyebrow="Content"
                title="Blog"
                subtitle="Manage public-facing blog posts. Markdown supported (## headings, - lists)."
                actions={
                    <Button onClick={openCreate} data-testid="blog-admin-new">
                        <Plus className="w-4 h-4 mr-1" /> New post
                    </Button>
                }
            />
            {!items ? (
                <LoadingBlock rows={4} />
            ) : (
                <Card>
                    <CardContent className="p-0">
                        <table className="w-full text-sm" data-testid="blog-admin-table">
                            <thead className="bg-secondary text-xs uppercase tracking-wider text-muted-foreground">
                                <tr>
                                    <th className="text-left py-2.5 px-4">Title</th>
                                    <th className="text-left py-2.5 px-4">Slug</th>
                                    <th className="text-left py-2.5 px-4">Status</th>
                                    <th className="text-left py-2.5 px-4">Updated</th>
                                    <th className="text-right py-2.5 px-4">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {items.map((p) => (
                                    <tr key={p.id} className="border-t border-border">
                                        <td className="py-2.5 px-4">
                                            <span className="mr-2">{p.cover_emoji}</span>{p.title}
                                        </td>
                                        <td className="py-2.5 px-4 text-xs font-mono text-muted-foreground">{p.slug}</td>
                                        <td className="py-2.5 px-4"><StatusChip status={p.status === "published" ? "connected" : "available"} label={p.status} /></td>
                                        <td className="py-2.5 px-4 text-xs text-muted-foreground">{fmtDate(p.updated_at)}</td>
                                        <td className="py-2.5 px-4 text-right space-x-1">
                                            <Button size="sm" variant="outline" onClick={() => openEdit(p)} data-testid="blog-admin-edit">
                                                <FileEdit className="w-3.5 h-3.5" />
                                            </Button>
                                            <Button size="sm" variant="outline" onClick={() => remove(p)} data-testid="blog-admin-delete">
                                                <Trash2 className="w-3.5 h-3.5" />
                                            </Button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </CardContent>
                </Card>
            )}

            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent className="max-w-2xl">
                    <DialogHeader>
                        <DialogTitle>{editingId ? "Edit post" : "New post"}</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-3">
                        <div className="grid grid-cols-[80px_1fr] gap-3">
                            <div>
                                <Label>Emoji</Label>
                                <Input value={edit.cover_emoji} onChange={(e) => setEdit({ ...edit, cover_emoji: e.target.value })} />
                            </div>
                            <div>
                                <Label>Title</Label>
                                <Input value={edit.title} onChange={(e) => setEdit({ ...edit, title: e.target.value })} data-testid="blog-admin-title" />
                            </div>
                        </div>
                        <div>
                            <Label>Slug <span className="text-xs text-muted-foreground">(optional, auto-generated from title)</span></Label>
                            <Input value={edit.slug} onChange={(e) => setEdit({ ...edit, slug: e.target.value })} placeholder="my-post-slug" />
                        </div>
                        <div>
                            <Label>Excerpt</Label>
                            <Textarea value={edit.excerpt} onChange={(e) => setEdit({ ...edit, excerpt: e.target.value })} rows={2} />
                        </div>
                        <div>
                            <Label>Body (markdown)</Label>
                            <Textarea value={edit.body_md} onChange={(e) => setEdit({ ...edit, body_md: e.target.value })} rows={10} data-testid="blog-admin-body" />
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <Label>Tags (comma-separated)</Label>
                                <Input value={edit.tags} onChange={(e) => setEdit({ ...edit, tags: e.target.value })} placeholder="strategy, ai" />
                            </div>
                            <div>
                                <Label>Status</Label>
                                <Select value={edit.status} onValueChange={(v) => setEdit({ ...edit, status: v })}>
                                    <SelectTrigger data-testid="blog-admin-status"><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="draft">Draft</SelectItem>
                                        <SelectItem value="published">Published</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
                        <Button onClick={save} data-testid="blog-admin-save">Save</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </>
    );
}
